import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Divider,
  IconButton,
  Tooltip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Save";
import clsx from "clsx";
import RichTextEditor from "../../../MUIRichTextEditor";
import { useStyles } from "./styles";
import QRCode from "react-qr-code";

const OfflineHelpButtonEditor = ({
  onSave,
  onCancel,
  initialMessage,
  initialQRCode,
  entityID
}) => {
  const classes = useStyles();
  const [messageText, setMessageText] = useState(initialMessage);
  const [qrCodeText, setQRCodeText] = useState(initialQRCode);
  const [hasChanges, setHasChanges] = useState(false);
  const initialMessageRef = React.useRef(initialMessage || "");
  const initialQRCodeRef = React.useRef(initialQRCode || "");
  

  useEffect(() => {
    window.handleBackButtonClick = handleCancel;
    return () => delete window.handleBackButtonClick;
  }, []);

  const handleMessageChange = async (newMessage) => {
    setMessageText(newMessage);
    setHasChanges(
      newMessage !== initialMessageRef.current ||
      qrCodeText !== initialQRCodeRef.current
    );
  };
  
  const handleQRCodeChange = async (event) => {
    const newQRCode = event.target.value;
    const limitedQRCode = (newQRCode || "").slice(0, 2000);
    setQRCodeText(limitedQRCode);
    setHasChanges(
      messageText !== initialMessageRef.current ||
      limitedQRCode !== initialQRCodeRef.current
    );
  };

  const handleSave = () => {
    onSave(messageText, qrCodeText);
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Box className={classes.helpButtonEditorContainer}>
      {/* Header */}
      <Box className={classes.helpButtonEditorHeader}>
        <Typography variant="h6" color="primary">
          Offline Help Button
        </Typography>
        <IconButton
          onClick={handleCancel}
          className={classes.closeButton}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Box className={classes.helpButtonEditorContent}>
        <Grid container spacing={3}>
          <Grid item xs className={classes.deviceMessageGrid}>
            <div className={clsx("offlineDeviceMessageEditor", classes.imgContainer, classes.helpButtonEditorWrapper)}>
              <RichTextEditor
                disabled={false}
                onChange={handleMessageChange}
                content={initialMessageRef.current}
                customEditorStyles={{}}
              />
              
              <div className={classes.qrCodeOverlay}>
                {qrCodeText && (
                  <div style={{
                    backgroundColor: "white",
                    padding: "12px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "1px solid #e0e0e0"
                  }}>
                    <QRCode value={qrCodeText} size={80} />
                  </div>
                )}
              </div>

              <Button
                variant="outlined"
                color="primary"
                size="small"
                className={classes.backButtonOverlay}
              >
                Cancel
              </Button>
            </div>
          </Grid>

          {/* QR Code Input Below Image */}
          <Grid item xs={12}>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Tooltip title="Converts plain text and symbols into a QR code that will be displayed with the message." placement="top">
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  label="Create QR Code"
                  placeholder="Enter text or symbols to generate QR code"
                  value={qrCodeText}
                  onChange={handleQRCodeChange}
                  inputProps={{
                    maxLength: 2000,
                    "data-testid": "qr-code-input"
                  }}
                  helperText={`${qrCodeText.length}/2000 characters`}
                  style={{ maxWidth: "400px" }}
                />
              </Tooltip>
            </div>
          </Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <Box className={classes.helpButtonEditorFooter}>
        <Divider />
        <Box className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!hasChanges}
          className={classes.saveButton}
          startIcon={<SaveIcon />}
        >
          Save
        </Button>

        <Button
          variant="outlined"
          onClick={handleCancel}
          className={classes.cancelButton}
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
        </Box>
      </Box>
    </Box>
  );
};

OfflineHelpButtonEditor.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialMessage: PropTypes.string,
  initialQRCode: PropTypes.string
};

OfflineHelpButtonEditor.defaultProps = {
  initialMessage: "",
  initialQRCode: ""
};

export default OfflineHelpButtonEditor;